// ─────────────────────────────────────────────────────────────────────────
// RolePermPanel — the role permission matrix (module × action grid).
// Shared between PermissionsPage.jsx (dedicated Roles & Permissions page)
// and RolesSection.jsx (the "Permissions" tab on a role's config modal) so
// there is exactly one implementation of load/toggle/save for role
// permissions — configure once per role, applies to every user with it.
// ─────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { Button, Spin, Tag, Tooltip, message } from 'antd';
import { CheckOutlined, EditOutlined, InfoCircleOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { getRoleId, getRolePermissions, updateRolePermissions } from '../../../../services/adminApi';
import '../../../../styles/PermissionsPage.css';

// ── Label helpers ─────────────────────────────────────────────────────────────

const KNOWN_KEYS = ['view', 'add', 'edit', 'delete'];

const DEFAULT_ACTION_LABELS = {
  view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete',
  sourcing: 'Sourcing', assign: 'Assign', share: 'Share',
  change_status: 'Status', my_jobs: 'My Jobs', all_jobs: 'All Jobs',
};

function defaultActionLabel(key) {
  return DEFAULT_ACTION_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function sortedPermKeys(keys) {
  const known = KNOWN_KEYS.filter((k) => keys.includes(k));
  const rest  = keys.filter((k) => !KNOWN_KEYS.includes(k)).sort();
  return [...known, ...rest];
}

export function allPermKeysFromData(data) {
  const set = new Set();
  Object.values(data).forEach(({ permissions }) =>
    Object.keys(permissions ?? {}).forEach((k) => set.add(k))
  );
  return sortedPermKeys([...set]);
}

export function countEnabled(data) {
  let enabled = 0, total = 0;
  Object.values(data).forEach(({ permissions }) => {
    Object.values(permissions ?? {}).forEach(({ value }) => {
      total++;
      if (value === '1') enabled++;
    });
  });
  return { enabled, total };
}

// ── Inline editable name ──────────────────────────────────────────────────────

function InlineName({ value, onSave, className }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  function commit() {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== value) onSave(t);
    else setDraft(value);
  }

  if (editing) {
    return (
      <input
        autoFocus
        className={`perm-inline-input${className ? ` ${className}` : ''}`}
        value={draft}
        style={{ width: Math.max(48, draft.length * 8) }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  commit();
          if (e.key === 'Escape') { setEditing(false); setDraft(value); }
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      className={`perm-editable-name${className ? ` ${className}` : ''}`}
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {value}
      <EditOutlined className="perm-name-edit-icon" />
    </span>
  );
}

// ── Action pill ───────────────────────────────────────────────────────────────

function ActionPill({ entry, label, onToggle, onLabelSave }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(label);
  const isOn      = entry?.value === '1';
  const available = !!entry;

  useEffect(() => { setDraft(label); }, [label]);

  function commitLabel() {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== label) onLabelSave(t);
    else setDraft(label);
  }

  return (
    <Tooltip title={available ? (isOn ? 'Click to disable' : 'Click to enable') : 'Not applicable for this module'}>
      <div
        className={`perm-action-pill${isOn ? ' on' : ''}${!available ? ' na' : ''}`}
        onClick={() => { if (!editing && available) onToggle(); }}
      >
        <span className={`perm-action-pill-dot${isOn ? ' on' : ''}`} />

        {editing ? (
          <input
            autoFocus
            className="perm-pill-input"
            value={draft}
            style={{ width: Math.max(36, draft.length * 7) }}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  commitLabel();
              if (e.key === 'Escape') { setEditing(false); setDraft(label); }
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="perm-action-pill-label"
            onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          >
            {label}
          </span>
        )}

        {isOn && !editing && <CheckOutlined className="perm-action-pill-check" />}
      </div>
    </Tooltip>
  );
}

// ── Module card ───────────────────────────────────────────────────────────────

function ModuleCard({ menuName, menuData, permKeys, onChange, displayName, actionLabels, onModuleLabelSave, onActionLabelSave }) {
  const perms   = menuData?.permissions ?? {};
  const onCount = Object.values(perms).filter((e) => e?.value === '1').length;
  const total   = Object.keys(perms).length;
  const allOn   = total > 0 && onCount === total;
  const someOn  = onCount > 0 && !allOn;

  return (
    <div className={`perm-module-card${allOn ? ' all-on' : someOn ? ' some-on' : ''}`}>
      <div className="perm-module-card-header">
        <InlineName
          value={displayName}
          onSave={(v) => onModuleLabelSave(menuName, v)}
          className="perm-module-card-name"
        />
        {total > 0 && (
          <span className={`perm-module-card-badge${onCount > 0 ? ' active' : ''}`}>
            {onCount}/{total}
          </span>
        )}
      </div>
      <div className="perm-module-card-body">
        {permKeys.map((k) => (
          <ActionPill
            key={k}
            entry={perms[k]}
            label={actionLabels[k] ?? defaultActionLabel(k)}
            onToggle={() => onChange(menuName, k)}
            onLabelSave={(v) => onActionLabelSave(k, v)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Role perm panel ───────────────────────────────────────────────────────────

export default function RolePermPanel({
  role,
  moduleLabels = {}, actionLabels = {},
  onModuleLabelSave = () => {}, onActionLabelSave = () => {},
}) {
  const [data,    setData]    = useState({});
  const [local,   setLocal]   = useState({});
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [dirty,   setDirty]   = useState(false);

  const roleId = getRoleId(role);

  useEffect(() => {
    if (!roleId) return;
    setLoading(true);
    setDirty(false);
    getRolePermissions(roleId)
      .then((d) => { setData(d); setLocal(d); })
      .catch(() => message.error('Failed to load permissions'))
      .finally(() => setLoading(false));
  }, [roleId]);

  function toggle(menuName, permKey) {
    setDirty(true);
    setLocal((prev) => {
      const menu  = prev[menuName] ?? { menuId: data[menuName]?.menuId ?? 0, permissions: {} };
      const entry = menu.permissions?.[permKey] ?? { id: 0, value: '0' };
      return {
        ...prev,
        [menuName]: {
          ...menu,
          permissions: {
            ...menu.permissions,
            [permKey]: { ...entry, value: entry.value === '1' ? '0' : '1' },
          },
        },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateRolePermissions(roleId, local);
      setData(local);
      setDirty(false);
      message.success('Permissions saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  const menuNames = useMemo(() => Object.keys(local).sort(), [local]);
  const permKeys  = useMemo(() => allPermKeysFromData(local), [local]);
  const { enabled, total } = useMemo(() => countEnabled(local), [local]);

  return (
    <Spin spinning={loading}>

      {/* ── Panel header ── */}
      <div className="perm-panel-header">
        <div className="perm-panel-title">
          <LockOutlined style={{ color: 'var(--color-brand)', marginRight: 6 }} />
          <span>{role?.roleName}</span>
          {!loading && total > 0 && (
            <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
              {enabled}/{total} enabled
            </Tag>
          )}
        </div>
        <Button
          type="primary"
          size="small"
          icon={<UnlockOutlined />}
          loading={saving}
          disabled={!dirty}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </div>

      {/* ── Hint ── */}
      <div className="perm-hint">
        <InfoCircleOutlined className="perm-hint-icon" />
        <span>
          Click a pill to toggle. <strong>Double-click</strong> a module name or action label to rename it — labels are saved locally.
        </span>
      </div>

      {/* ── Module cards grid ── */}
      {menuNames.length === 0 && !loading ? (
        <div className="perm-empty">No permission entries found for this role.</div>
      ) : (
        <div className="perm-cards-grid">
          {menuNames.map((menuName) => (
            <ModuleCard
              key={menuName}
              menuName={menuName}
              menuData={local[menuName]}
              permKeys={permKeys}
              onChange={toggle}
              displayName={moduleLabels[menuName] ?? menuName}
              actionLabels={actionLabels}
              onModuleLabelSave={onModuleLabelSave}
              onActionLabelSave={onActionLabelSave}
            />
          ))}
        </div>
      )}

    </Spin>
  );
}
